import React, { useState, useCallback } from 'react';
import {
  Box,
  VStack,
  Heading,
  Button,
  StackDivider,
  HStack,
  useDisclosure,
  Flex,
  Spinner,
  Text,
  Thead,
  Tr,
  Th,
  TableContainer,
  Table,
  Tbody,
  Td
} from '@chakra-ui/react';
import {
  SETTLEMENT_SERVICE_DASHBOARD_STRINGS,
  TAB_STATES
} from '@views/SettlementServiceDashboard/constant';
import { MdAdd, MdVisibility } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { updateSettlementServiceClientsReqData } from '@store/actions/reqDataActions';
import useDebounce from '@hooks/useDebounce';
import SearchBox from '@components/SearchBox/SearchBox';
import Pagination from '@components/Pagination';
import { getAllClients } from '@store/actions/settlementServiceDashboardActions';
import useDidMountEffect from '@hooks/useDidMount';
import { formatNumberWithCommas } from '@utils';
import DynamicInputModal from '@components/DynamicInputModal';
import { clientSettlementsConfig as config } from '@views/SettlementServiceDashboard/utils';
import ModalLayout from '@components/Modal/ModalLayout';
import CopyableText from '@components/CopyableText';

const ClientSettlements = ({ setSelectedClient, selectedClient }) => {
  const dispatch = useDispatch();

  const reqData = useSelector(
    (state) => state.reqData.settlementServiceDashboard.clients
  );
  const { filters, searches, totalPages, pageNumber } = reqData;

  const {
    data: clientSettlements,
    isLoading,
    error
  } = useSelector((state) => state.settlementServiceDashboard.clients);

  // Get common data for dropdown options
  const commonData = useSelector(
    (state) => state.settlementServiceDashboard.commons.data
  );

  const [isModalLoading, setIsModalLoading] = useState(false);
  const [filterParams, setFilterParams] = useState(filters || {});
  const [searchParams, setSearchParams] = useState({});

  const debouncedFilterParams = useDebounce(filterParams, 500);
  const debouncedSearchParams = useDebounce(searchParams, 500);

  const {
    isOpen: isCreateModalOpen,
    onClose: onCreateModalClose,
    onOpen: openCreateModal
  } = useDisclosure();

  const STRING_CONSTANTS =
    SETTLEMENT_SERVICE_DASHBOARD_STRINGS.STRING_CONSTANTS[TAB_STATES.CLIENT];

  const fetchClientSettlements = useCallback(() => {
    dispatch(
      getAllClients({
        payload: reqData
      })
    );
  }, [dispatch, reqData]);

  useDidMountEffect(() => {
    fetchClientSettlements();
  }, [debouncedFilterParams, debouncedSearchParams, pageNumber]);

  // Handle Page Change
  const handlePageChange = useCallback(
    (page) => {
      const newPageNumber = Math.max(0, page - 1);
      dispatch(
        updateSettlementServiceClientsReqData({
          ...reqData,
          pageNumber: newPageNumber
        })
      );
    },
    [dispatch, reqData]
  );

  const handleSearchChange = (search) => {
    setSearchParams((prev) => ({
      ...prev,
      searchKey: search.searchKey,
      searchValue: search.searchValue
    }));
    dispatch(
      updateSettlementServiceClientsReqData({
        ...reqData,
        searchKey: search.searchKey,
        searchValue: search.searchValue,
        pageNumber: 0
      })
    );
  };

  const handleViewClientDetails = (client) => setSelectedClient(client);

  const handleInputModalSubmit = (formData) => {
    setIsModalLoading(true);
    // NOTE :: Consider that the payload converter is åccepting an array of payout partner
    dispatch(
      config.addNewClientSettlement({
        payload: config.addClientSettlementPayloadConvertor({ formData }),
        onSuccess: () => {
          fetchClientSettlements();
        },
        onComplete: () => {
          setIsModalLoading(false);
        }
      })
    );
    onCreateModalClose();
  };

  return (
    <Box h="100%" overflow="hidden">
      <ModalLayout isOpen={isCreateModalOpen} onClose={onCreateModalClose}>
        <DynamicInputModal
          title={STRING_CONSTANTS.INPUT_MODAL_TITLE}
          isOpen={isCreateModalOpen}
          onClose={onCreateModalClose}
          onSubmit={handleInputModalSubmit}
          fields={config.newClientSettlementFieldsConfig(commonData) || []}
          isLoading={isModalLoading}
        />
      </ModalLayout>

      <VStack
        h="100%"
        overflowY="auto"
        overflowX="hidden"
        alignItems="stretch"
        gap={4}
        px={4}
        pb={4}
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        <HStack justify="space-between">
          <Heading size="md">Clients</Heading>
          <Button
            colorScheme="brand"
            size="sm"
            bg="backGround"
            border="2px solid"
            borderColor="whiteAlpha.300"
            borderRadius="lg"
            px={4}
            leftIcon={<MdAdd size="1.5rem" />}
            onClick={openCreateModal}
          >
            {STRING_CONSTANTS.ADD_NEW_CLIENT}
          </Button>
        </HStack>

        {/* Search */}
        {!!searches?.length && (
          <SearchBox
            searches={searches}
            handleSearchChange={handleSearchChange}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            onPageChange={handlePageChange}
            currentPage={pageNumber + 1}
            totalPages={totalPages}
          />
        )}

        {/* Table / States */}
        {isLoading ? (
          <Flex justify="center" align="center" w="full" h="full">
            <Spinner thickness="4px" size="lg" color="colorPrimary" />
          </Flex>
        ) : error ? (
          <Box
            p={4}
            bg="backGround"
            border="1px solid"
            borderColor="whiteAlpha.300"
            borderRadius="xl"
          >
            <Text color="red.500">Error: {error}</Text>
          </Box>
        ) : clientSettlements?.length ? (
          <>
            <Heading fontSize="lg" color="colorPrimary">
              Client Settlements
            </Heading>
            <TableContainer
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="xl"
              my={4}
              overflow="visible"
            >
              <Table variant="unstyled">
                <Thead>
                  <Tr
                    bg="backGround"
                    borderBottom="2px solid"
                    borderColor="whiteAlpha.500"
                  >
                    <Th textAlign="center" whiteSpace="nowrap">
                      Client ID
                    </Th>
                    <Th textAlign="center" whiteSpace="nowrap">
                      Amount to Settle
                    </Th>
                    <Th textAlign="center" whiteSpace="nowrap">
                      Action
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {clientSettlements.map((client) => (
                    <Tr
                      key={client.clientId}
                      bg={
                        selectedClient?.clientId === client.clientId
                          ? '#1a2e1a'
                          : '#1c1c1c'
                      }
                      border="3px solid black"
                      _hover={{ bg: '#111111' }}
                      position="relative"
                      zIndex={
                        selectedClient?.clientId === client.clientId ? 1 : 0
                      }
                      transition="all 0.3s ease"
                    >
                      <Td textAlign="center" whiteSpace="nowrap">
                        <CopyableText
                          title="Client ID"
                          fontSize="xs"
                          color="white"
                        >
                          {client.clientId ?? '-'}
                        </CopyableText>
                      </Td>
                      <Td textAlign="center" whiteSpace="nowrap">
                        <Text fontSize="xs">
                          {client?.totalAmountToSettle !== null && client?.totalAmountToSettle !== undefined
                              ? formatNumberWithCommas(client?.totalAmountToSettle)
                              : '-'}
                        </Text>
                      </Td>
                      <Td textAlign="center" whiteSpace="nowrap">
                        <Button
                          colorScheme="brand"
                          size="xs"
                          bg="black"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          borderRadius="lg"
                          px={2}
                          leftIcon={<MdVisibility size="1rem" />}
                          onClick={() => handleViewClientDetails(client)}
                        >
                          View Details
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box
            p={4}
            bg="backGround"
            border="1px solid"
            borderColor="whiteAlpha.300"
            borderRadius="xl"
          >
            <Text>No Client Settlements Found!</Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default ClientSettlements;
